'use client'

import React, { useState, useEffect, useCallback } from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { ChevronDown, Check } from 'lucide-react'
import { PhoneData, BaseFieldProps } from '../types'
import { parsePhoneNumber, validatePhoneNumber } from '../utils/validation'
import { cn, baseInputStyles, inputVariants, labelStyles, errorStyles, helperTextStyles } from '../utils/styles'

export interface PhoneInputProps extends BaseFieldProps {
  value: string
  onChange: (value: string) => void
  onValidChange?: (isValid: boolean, phoneData: PhoneData) => void
  defaultCountry?: string
  autoFormat?: boolean
  showValidationIcon?: boolean
  validateOnChange?: boolean
  placeholder?: string
}

// Country codes and names - comprehensive list for international support
const COUNTRIES = [
  // Primary countries (Ballee operates in these)
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭', dialCode: '+41' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '+49' },
  { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '+33' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', dialCode: '+39' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', dialCode: '+34' },

  // European countries
  { code: 'AT', name: 'Austria', flag: '🇦🇹', dialCode: '+43' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪', dialCode: '+32' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱', dialCode: '+31' },
  { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', dialCode: '+352' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪', dialCode: '+353' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹', dialCode: '+351' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪', dialCode: '+46' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴', dialCode: '+47' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰', dialCode: '+45' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮', dialCode: '+358' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱', dialCode: '+48' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', dialCode: '+420' },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺', dialCode: '+36' },
  { code: 'RO', name: 'Romania', flag: '🇷🇴', dialCode: '+40' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷', dialCode: '+30' },

  // Americas
  { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', dialCode: '+52' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', dialCode: '+55' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', dialCode: '+54' },

  // Asia-Pacific
  { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '+61' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', dialCode: '+64' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '+81' },
  { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '+86' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', dialCode: '+82' },
  { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', dialCode: '+65' },

  // Middle East & Africa
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', dialCode: '+971' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱', dialCode: '+972' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', dialCode: '+27' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬', dialCode: '+20' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷', dialCode: '+90' },

  // Eastern Europe & CIS
  { code: 'RU', name: 'Russia', flag: '🇷🇺', dialCode: '+7' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦', dialCode: '+380' },
].sort((a, b) => {
  // Keep Switzerland first, then sort alphabetically
  if (a.code === 'CH') return -1
  if (b.code === 'CH') return 1
  return a.name.localeCompare(b.name)
})

export function PhoneInput({
  value,
  onChange,
  onValidChange,
  defaultCountry = 'CH',
  autoFormat = true,
  showValidationIcon = false,
  validateOnChange = true,
  placeholder = '+41 76 123 45 67',
  label = 'Phone Number',
  required = false,
  disabled = false,
  className,
  error,
  helperText
}: PhoneInputProps) {
  const [phoneData, setPhoneData] = useState<PhoneData>({ countryCode: '', nationalNumber: '', formattedNumber: value, isValid: false })
  const [touched, setTouched] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry)

  // Parse and validate phone number whenever value changes
  useEffect(() => {
    if (value) {
      const parsed = parsePhoneNumber(value, selectedCountry)
      setPhoneData(parsed)

      if (validateOnChange && touched && onValidChange) {
        onValidChange(parsed.isValid, parsed)
      }
    } else {
      setPhoneData({ countryCode: '', nationalNumber: '', formattedNumber: '', isValid: false })
      if (onValidChange) {
        onValidChange(false, { countryCode: '', nationalNumber: '', formattedNumber: '', isValid: false })
      }
    }
  }, [value, selectedCountry, touched, validateOnChange, onValidChange])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value

    if (autoFormat && newValue) {
      const parsed = parsePhoneNumber(newValue, selectedCountry)
      if (parsed.isValid) {
        onChange(parsed.formattedNumber)
      } else {
        onChange(newValue)
      }
    } else {
      onChange(newValue)
    }
  }, [autoFormat, selectedCountry, onChange])

  const handleBlur = useCallback(() => {
    setTouched(true)
    if (onValidChange) {
      onValidChange(phoneData.isValid, phoneData)
    }
  }, [phoneData, onValidChange])

  const handleCountrySelect = useCallback((countryCode: string) => {
    setSelectedCountry(countryCode)

    // If there's a value, reparse it with the new country
    if (value) {
      const parsed = parsePhoneNumber(value, countryCode)
      if (autoFormat && parsed.isValid) {
        onChange(parsed.formattedNumber)
      }
    }
  }, [value, autoFormat, onChange])

  const validation = validatePhoneNumber(value, selectedCountry)
  const showError = touched && (error || (!validation.isValid && value))
  const showSuccess = showValidationIcon && touched && validation.isValid && !error
  const selectedCountryData = COUNTRIES.find(c => c.code === selectedCountry) || COUNTRIES[0]

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label htmlFor="phone" className={labelStyles}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative flex">
        {/* Country Selector */}
        <SelectPrimitive.Root
          value={selectedCountry}
          onValueChange={handleCountrySelect}
          disabled={disabled}
        >
          <SelectPrimitive.Trigger
            className={cn(
              'flex items-center px-3 py-2 border border-r-0 rounded-l-md text-sm bg-gray-50 hover:bg-gray-100 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              showError ? 'border-red-500' : 'border-gray-300',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <span className="mr-2">{selectedCountryData.flag}</span>
            <span className="text-gray-600">{selectedCountryData.dialCode}</span>
            <SelectPrimitive.Icon className="ml-1">
              <ChevronDown className="h-3 w-3 text-gray-400" />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              className="z-50 w-64 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
              position="popper"
              sideOffset={4}
            >
              <SelectPrimitive.ScrollUpButton className="flex items-center justify-center h-6 cursor-default">
                <ChevronDown className="h-3 w-3 rotate-180" />
              </SelectPrimitive.ScrollUpButton>

              <SelectPrimitive.Viewport className="max-h-60 overflow-y-auto">
                {COUNTRIES.map((country) => (
                  <SelectPrimitive.Item
                    key={country.code}
                    value={country.code}
                    className={cn(
                      'relative flex items-center px-3 py-2 text-sm cursor-pointer select-none',
                      'focus:bg-gray-50 focus:outline-none',
                      'data-[highlighted]:bg-gray-50',
                      'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none'
                    )}
                  >
                    <span className="mr-3">{country.flag}</span>
                    <span className="flex-1">{country.name}</span>
                    <span className="text-gray-500 mr-6">{country.dialCode}</span>
                    <SelectPrimitive.ItemIndicator className="absolute right-2">
                      <Check className="h-3 w-3" />
                    </SelectPrimitive.ItemIndicator>
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.Viewport>

              <SelectPrimitive.ScrollDownButton className="flex items-center justify-center h-6 cursor-default">
                <ChevronDown className="h-3 w-3" />
              </SelectPrimitive.ScrollDownButton>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>

        {/* Phone Input */}
        <div className="relative flex-1">
          <input
            id="phone"
            type="tel"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={cn(
              baseInputStyles,
              'rounded-l-none',
              showError ? inputVariants.error :
              showSuccess ? inputVariants.success : inputVariants.default,
              disabled && inputVariants.disabled
            )}
            aria-invalid={!!showError}
            aria-describedby={
              showError ? 'phone-error' : helperText ? 'phone-helper' : undefined
            }
          />

          {/* Validation Icons */}
          {showValidationIcon && (showSuccess || showError) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {showSuccess && (
                <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {showError && (
                <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {showError && (
        <p id="phone-error" className={errorStyles}>
          {error || validation.error}
        </p>
      )}

      {/* Helper Text */}
      {helperText && !showError && (
        <p id="phone-helper" className={helperTextStyles}>
          {helperText}
        </p>
      )}

      {/* Phone Data Debug (only in development) */}
      {process.env.NODE_ENV === 'development' && phoneData.isValid && (
        <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
          <div>Country: {phoneData.countryCode}</div>
          <div>National: {phoneData.nationalNumber}</div>
          <div>Formatted: {phoneData.formattedNumber}</div>
          <div>Valid: {phoneData.isValid ? '✅' : '❌'}</div>
        </div>
      )}
    </div>
  )
}

export default PhoneInput